import { computed, makeObservable, observable } from 'mobx';

import { toError } from 'helpers/toError';

/** Stable per-item identity. Object identity is never used — see {@link SubsetModel}. */
export type SubsetKey = string | number;

/**
 * Where an `EditSubset`'s universe comes from: an array already in memory, or a loader called once
 * when the model is built. Pass a **stable** loader reference (`useMemo` at the call site) — the
 * control rebuilds its model whenever `source` changes, and a rebuild re-fetches.
 *
 * There is deliberately no server-side `search` kind (contrast `ReferenceSource`): this control
 * holds its universe whole. See `../README.md` for the query-primary / scroll-primary split.
 */
export type SubsetSource<T> = readonly T[] | (() => Promise<readonly T[]>);

// The universe as one value rather than three fields: the states are mutually exclusive, so there
// is no "loading with a stale error" or "failed but still holding items" gap to reason about.
type Universe<T> =
  { readonly kind: 'loading' } | { readonly kind: 'loaded'; readonly items: readonly T[] } | { readonly kind: 'failed'; readonly error: Error };

/**
 * The engine behind `EditSubset`: the loaded universe, the filter text, and the derivations the two
 * panes render. All of the control's logic lives here so the view can be a pure `observer` — and so
 * the invariants below are unit-testable without RTL.
 *
 * **The model mirrors the controlled `value`; it does not own it.** `syncValue` is called from the
 * component whenever the prop changes, and the mutators (`addAllShown` / `clear` / `toggle`) only
 * *compute* the next value for the caller to forward to `onChange`. A parent that ignores that
 * `onChange` therefore sees the control stay put, as a controlled component should. (`ReferenceModel`
 * takes the opposite, optimistic route because a typeahead's input text would otherwise flicker;
 * nothing here is typed into, so there is nothing to flicker.)
 *
 * **`selected` is derived from the value, never from the loaded items.** A value item matching no
 * loaded item — a lock deleted since it was stored, or one in a zone this user may not list — still
 * renders, flagged by `isOrphan`, so it survives the round trip rather than being silently dropped
 * on the next save.
 *
 * **Item identity is `getKey`.** Values handed in by the caller and items arriving from the source
 * are built by different routes and never share references.
 */
export class SubsetModel<T> {
  private _filter = '';
  private _universe: Universe<T>;
  private _value: readonly T[] = [];

  constructor(
    source: SubsetSource<T>,
    private readonly _getKey: (item: T) => SubsetKey,
    private readonly _getLabel: (item: T) => string
  ) {
    this._universe = typeof source === 'function' ? { kind: 'loading' } : { kind: 'loaded', items: source };
    makeObservable<SubsetModel<T>, '_filter' | '_itemKeys' | '_selectedKeys' | '_universe' | '_value'>(this, {
      _filter: observable,
      _itemKeys: computed,
      _selectedKeys: computed,
      _universe: observable.ref,
      _value: observable.ref,
      selected: computed,
      shown: computed,
    });

    if (typeof source === 'function') {
      source().then(
        (items) => {
          this._universe = { kind: 'loaded', items };
        },
        (err: unknown) => {
          this._universe = { kind: 'failed', error: toError(err) };
        }
      );
    }
  }

  /** False when every shown item is already selected (or none is shown) — nothing left to add. */
  get canAddAllShown(): boolean {
    const selected = this._selectedKeys;
    return this.shown.some((item) => !selected.has(this._getKey(item)));
  }

  /** The text narrowing the left pane. Matched as a case-insensitive substring of the label. */
  get filter(): string {
    return this._filter;
  }
  setFilter = (filter: string): void => {
    this._filter = filter;
  };

  /** True while the source's loader is in flight. */
  get isLoading(): boolean {
    return this._universe.kind === 'loading';
  }

  private get _itemKeys(): ReadonlySet<SubsetKey> {
    return new Set(this._items.map((item) => this._getKey(item)));
  }

  private get _items(): readonly T[] {
    return this._universe.kind === 'loaded' ? this._universe.items : [];
  }

  /** What the loader rejected with, or null. The selection stays readable and removable either way. */
  get loadError(): Error | null {
    return this._universe.kind === 'failed' ? this._universe.error : null;
  }

  /** Every item in the controlled value, in display order. */
  get selected(): readonly T[] {
    return [...this._value].sort(this._compare);
  }

  private get _selectedKeys(): ReadonlySet<SubsetKey> {
    return new Set(this._value.map((item) => this._getKey(item)));
  }

  /** What the left pane lists: the universe narrowed by the filter, in display order. */
  get shown(): readonly T[] {
    const query = this._filter.trim().toLowerCase();
    const matching = query === '' ? this._items : this._items.filter((item) => this._getLabel(item).toLowerCase().includes(query));
    return [...matching].sort(this._compare);
  }

  /** The value with every currently shown item added. */
  addAllShown = (): readonly T[] => {
    const selected = this._selectedKeys;
    return [...this._value, ...this.shown.filter((item) => !selected.has(this._getKey(item)))];
  };

  /** The empty value. */
  clear = (): readonly T[] => [];

  // Display order for both panes: by label, then by key so items sharing a label still have a
  // deterministic order (two zones can hold the same lock name).
  private _compare = (a: T, b: T): number =>
    this._getLabel(a).localeCompare(this._getLabel(b)) || String(this._getKey(a)).localeCompare(String(this._getKey(b)));

  /**
   * True when a selected item matches nothing in the loaded universe, i.e. it is shown in the
   * selection pane but has no row to tick on the left.
   *
   * False while the universe is unknown — loading, or the load failed — because every item would
   * otherwise flag as orphaned for as long as the fetch is in flight.
   */
  isOrphan = (item: T): boolean => this._universe.kind === 'loaded' && !this._itemKeys.has(this._getKey(item));

  isSelected = (item: T): boolean => this._selectedKeys.has(this._getKey(item));

  /** Mirror the controlled value. Call whenever the prop changes. */
  syncValue = (value: readonly T[]): void => {
    this._value = value;
  };

  /**
   * The value with `item` removed if it is selected, else appended.
   *
   * Additions append rather than insert in display order: the stored order is the caller's, and
   * reordering it on every tick would read as a change to a dirty check.
   */
  toggle = (item: T): readonly T[] => {
    const key = this._getKey(item);
    return this._selectedKeys.has(key) ? this._value.filter((selected) => this._getKey(selected) !== key) : [...this._value, item];
  };
}
